import { needsMemberMutationKey, newMutationKey } from "./requestIdempotency";
/** Shared cookie/embed transport, with URL-admitted headers only in canonical mode. */
import { getEmbedSessionJwt, recoverFromEmbedAuthFailure } from "../config/axios";
import { assertApiOrigin } from "../config/apiOrigin";
import {
    applyTenantHeaderPolicy,
    assertTenantRequestScope,
    captureTenantScopeGeneration,
    isTenantScopeRequired,
    isUnvalidatedTenantRequestAllowed,
    isSourceCredentialRequest,
    releaseScopeRequestSignal,
    scopeRequestSignal,
} from "../contexts/tenantScopeHeaders";
import axios from "axios";
import { getPlatform } from "../platform/platformRef";
import { captureAccessTicket } from "../access/resourceAccess";
import { fenceFetchResponse } from "../access/fetchFence";
import { REFUSAL } from "../utils/admissionRefusal";
import { renewForDisabledRoute } from "../contexts/tabCompanySession";
import { applyClientDeclarationHeaders } from "../api/clientDeclaration";
import { forwardAppUpdate } from "../api/appUpdate";

interface OpenAuthenticatedSseResponseOptions {
    signal: AbortSignal;
    lastEventId?: string | null;
    sessionJwt?: string | null;
    withCredentials?: boolean;
}
function headersFor(
    init: HeadersInit | undefined,
    jwt: string | null,
    admissionRequest: boolean,
): Headers {
    const headers = new Headers(init);
    applyClientDeclarationHeaders(headers);
    if (isTenantScopeRequired()) applyTenantHeaderPolicy(headers, admissionRequest);
    if (jwt) headers.set("Authorization", `Bearer ${jwt}`);
    return headers;
}
function assertRequest(url: string, generation: number): void {
    assertTenantRequestScope(url, generation);
    if (!isTenantScopeRequired()) return;
    assertApiOrigin(new URL(url, axios.defaults.baseURL || window.location.origin));
}
export async function openAuthenticatedSseResponse(
    url: string,
    options: OpenAuthenticatedSseResponseOptions,
): Promise<{ response: Response; usedEmbedAuth: boolean }> {
    const captured = captureTenantScopeGeneration();
    const ticket = captureAccessTicket();
    assertRequest(url, captured);
    let sessionJwt =
        isTenantScopeRequired() || options.sessionJwt === undefined
            ? await getEmbedSessionJwt()
            : options.sessionJwt;
    let alreadyRetried = false;
    // The composite of the attempt that is still open stays registered: its
    // body is consumed by the caller for the life of the stream, so the scope
    // signal must keep reaching it. Every superseded attempt releases here.
    let openSignal: AbortSignal | undefined;
    const release = () => {
        releaseScopeRequestSignal(openSignal);
        openSignal = undefined;
    };
    try {
        while (true) {
            assertRequest(url, captured);
            const headers = headersFor(undefined, sessionJwt, false);
            headers.set("Accept", "text/event-stream");
            if (options.lastEventId) headers.set("Last-Event-ID", options.lastEventId);
            release();
            openSignal = scopeRequestSignal(options.signal);
            const response = await fetch(url, {
                credentials: isTenantScopeRequired()
                    ? "omit"
                    : options.withCredentials === undefined
                      ? getPlatform().auth.credentials
                      : options.withCredentials
                        ? "include"
                        : "omit",
                signal: openSignal,
                headers,
            });
            assertRequest(url, captured);
            const usedEmbedAuth = sessionJwt !== null;
            if (response.status !== 401 || !usedEmbedAuth) {
                openSignal = undefined;
                followErrorCode(response);
                return { response: fenceFetchResponse(response, ticket), usedEmbedAuth };
            }
            const shouldRetry = await recoverFromEmbedAuthFailure(alreadyRetried);
            assertRequest(url, captured);
            if (!shouldRetry || alreadyRetried) {
                openSignal = undefined;
                return { response: fenceFetchResponse(response, ticket), usedEmbedAuth };
            }
            await response.body?.cancel();
            alreadyRetried = true;
            sessionJwt = await getEmbedSessionJwt();
        }
    } finally {
        release();
    }
}

/**
 * A request carried by the browser's own sign-in (the source cookie, or the embed/native source
 * JWT) with no project selectors: what a bootstrap request sends. For a URL tab with no admitted
 * project, whose request fence refuses everything but bootstrap (an admission notice's product
 * event); the server reads it as a request of the source session's own organization.
 */
export async function sourceCredentialFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
): Promise<Response> {
    const jwt = await getEmbedSessionJwt(true);
    return fetch(input, {
        ...init,
        credentials: jwt ? "omit" : getPlatform().auth.credentials,
        headers: headersFor(init?.headers, jwt, true),
    });
}

/** What a response's error code asks of the page, read from the header alone, before the access
 *  fence may cancel a denied body unopened: a disabled project route renews the tab
 *  (`renewForDisabledRoute`), and a floored iOS build shows the native update screen. */
function followErrorCode(response: Response): void {
    if (
        response.status === 403 &&
        response.headers.get("x-primepoint-error-code") === REFUSAL.disabled
    )
        renewForDisabledRoute();
    forwardAppUpdate(response.status, (name) => response.headers.get(name));
}

/**
 * Fetch with authentication headers included.
 * - credentials from the installed host, unless a canonical project credential is in play
 * - Authorization: Bearer header for embed contexts
 */
export async function authenticatedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
): Promise<Response> {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const captured = captureTenantScopeGeneration();
    const ticket = captureAccessTicket();
    assertRequest(url, captured);
    const bootstrap = isSourceCredentialRequest(url);
    const jwt = await getEmbedSessionJwt(bootstrap);
    assertRequest(url, captured);
    const headers = headersFor(init?.headers, jwt, isUnvalidatedTenantRequestAllowed(url));
    if (
        isTenantScopeRequired() &&
        needsMemberMutationKey(url, init?.method) &&
        !headers.has("Idempotency-Key")
    )
        headers.set("Idempotency-Key", newMutationKey());
    const signal = scopeRequestSignal(init?.signal);
    try {
        const response = await fetch(input, {
            ...init,
            credentials: isTenantScopeRequired() && jwt ? "omit" : getPlatform().auth.credentials,
            signal,
            headers,
        });
        assertRequest(url, captured);
        followErrorCode(response);
        return fenceFetchResponse(response, ticket);
    } finally {
        // The request has settled; the caller's own signal still governs the
        // body it reads next, and the assert above already fenced the response
        // against a scope change.
        releaseScopeRequestSignal(signal);
    }
}
